import { useState } from 'react';
import { Gift, Copy, Check, Users, Calendar, TrendingUp, Info } from 'lucide-react';
import { useReferralMe } from '../hooks/useReferralMe';

const MAX_MONTHS = 6;

export function ReferralPage() {
  const { data, loading, error } = useReferralMe();
  const [copied, setCopied] = useState(false);

  async function handleCopy() {
    if (!data?.referral_link) return;
    try {
      await navigator.clipboard.writeText(data.referral_link);
    } catch {
      const input = document.createElement('input');
      input.value = data.referral_link;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  function formatDate(iso: string) {
    return new Date(iso).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  }

  function formatAmount(cents: number, currency: string) {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(cents / 100);
  }

  const monthsUsed = data?.stats.months_won ?? 0;
  const capPercent = Math.min((monthsUsed / MAX_MONTHS) * 100, 100);

  return (
    <div className="space-y-6 max-w-3xl mx-auto">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-blue-50 rounded-lg">
            <Gift size={24} className="text-blue-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Programme de parrainage</h2>
            <p className="text-sm text-gray-500">Recommandez TrezoOne, gagnez des mois gratuits</p>
          </div>
        </div>
      </div>

      <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl p-6 text-white">
        <h3 className="text-lg font-bold mb-1">Comment ça marche ?</h3>
        <p className="text-blue-100 text-sm mb-4">
          Partagez votre lien. Quand votre filleul souscrit un abonnement payant, vous recevez automatiquement <strong className="text-white">1 mois offert</strong> déduit de votre prochaine facture.
        </p>
        <div className="flex flex-col sm:flex-row gap-3">
          {[
            { step: '1', label: 'Copiez votre lien' },
            { step: '2', label: 'Votre filleul s\'inscrit' },
            { step: '3', label: 'Il souscrit un abonnement' },
            { step: '4', label: '1 mois offert pour vous' },
          ].map(({ step, label }) => (
            <div key={step} className="flex items-center gap-2 flex-1">
              <span className="flex-shrink-0 w-7 h-7 bg-white/20 rounded-full flex items-center justify-center text-sm font-bold">
                {step}
              </span>
              <span className="text-sm text-blue-100">{label}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <h3 className="text-base font-semibold text-gray-800 mb-3">Votre lien de parrainage</h3>
        {loading ? (
          <div className="h-12 bg-gray-100 rounded-lg animate-pulse" />
        ) : error ? (
          <p className="text-sm text-red-500">{error}</p>
        ) : (
          <div className="flex gap-2">
            <div className="flex-1 min-w-0 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
              <span className="text-sm text-gray-700 font-mono block truncate">
                {data?.referral_link}
              </span>
            </div>
            <button
              onClick={handleCopy}
              className={`flex-shrink-0 flex items-center gap-2 px-5 py-3 rounded-lg font-semibold text-sm transition-all ${
                copied
                  ? 'bg-green-500 text-white'
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
            >
              {copied ? <Check size={16} /> : <Copy size={16} />}
              {copied ? 'Copié !' : 'Copier'}
            </button>
          </div>
        )}
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 text-center">
          <div className="flex items-center justify-center gap-2 mb-2 text-gray-500">
            <Users size={16} />
            <span className="text-xs font-medium uppercase tracking-wide">Clics</span>
          </div>
          <p className="text-3xl font-bold text-gray-900">
            {loading ? '—' : data?.stats.clicks ?? 0}
          </p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 text-center">
          <div className="flex items-center justify-center gap-2 mb-2 text-gray-500">
            <TrendingUp size={16} />
            <span className="text-xs font-medium uppercase tracking-wide">Inscrits</span>
          </div>
          <p className="text-3xl font-bold text-gray-900">
            {loading ? '—' : data?.stats.signups ?? 0}
          </p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 text-center">
          <div className="flex items-center justify-center gap-2 mb-2 text-gray-500">
            <Calendar size={16} />
            <span className="text-xs font-medium uppercase tracking-wide">Mois gagnés</span>
          </div>
          <p className="text-3xl font-bold text-blue-600">
            {loading ? '—' : monthsUsed}
          </p>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-semibold text-gray-800">Plafond annuel</h3>
          <span className="text-sm text-gray-500">
            {monthsUsed} / {MAX_MONTHS} mois sur 12 mois glissants
          </span>
        </div>
        <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden">
          <div
            className="h-3 rounded-full transition-all bg-blue-500"
            style={{ width: `${capPercent}%` }}
          />
        </div>
        {monthsUsed >= MAX_MONTHS && (
          <div className="mt-3 flex items-start gap-2 text-sm text-amber-700 bg-amber-50 rounded-lg p-3">
            <Info size={16} className="flex-shrink-0 mt-0.5" />
            <span>Vous avez atteint le plafond de 6 mois offerts sur les 12 derniers mois. Les nouvelles récompenses reprendront dès qu'un mois sortira de la période glissante.</span>
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <h3 className="text-base font-semibold text-gray-800 mb-4">Historique des récompenses</h3>
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-14 bg-gray-100 rounded-lg animate-pulse" />
            ))}
          </div>
        ) : !data?.history.length ? (
          <div className="text-center py-10 text-gray-400">
            <Gift size={32} className="mx-auto mb-2 opacity-40" />
            <p className="text-sm">Aucune récompense pour l'instant.</p>
            <p className="text-xs mt-1">Partagez votre lien pour commencer à parrainer !</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {data.history.map(item => (
              <div key={item.id} className="py-3 flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-green-50 rounded-full flex items-center justify-center flex-shrink-0">
                    <Gift size={14} className="text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-800">
                      {item.months} mois offert{item.months > 1 ? 's' : ''}
                    </p>
                    {item.referred_email && (
                      <p className="text-xs text-gray-400">{item.referred_email}</p>
                    )}
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-sm font-semibold text-green-600">
                    -{formatAmount(item.amount_cents, item.currency)}
                  </p>
                  <p className="text-xs text-gray-400">{formatDate(item.date)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-gray-50 rounded-xl border border-gray-200 p-5">
        <h3 className="text-sm font-semibold text-gray-700 mb-2">Conditions du programme</h3>
        <ul className="text-xs text-gray-500 space-y-1 list-disc list-inside">
          <li>La récompense est créditée dès le premier paiement réussi du filleul.</li>
          <li>Maximum 6 mois offerts par utilisateur sur toute période de 12 mois glissants.</li>
          <li>Le crédit est appliqué directement sur votre solde Stripe (déducté de votre prochaine facture).</li>
          <li>L'auto-parrainage n'est pas autorisé.</li>
        </ul>
      </div>
    </div>
  );
}
